// src/components/MarketHeatHistoryChart.tsx

import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  Plugin
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { format } from 'date-fns';
import { MarketHeatHistoryEntry, HistoryTimeRange } from '../types';
import { EthPriceHistoryEntry } from '../services/marketHeatHistory';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface MarketHeatHistoryChartProps {
  data: MarketHeatHistoryEntry[];
  ethPriceData: EthPriceHistoryEntry[];
  loading: boolean;
  error: string | null;
  timeRange: HistoryTimeRange;
  onTimeRangeChange: (range: HistoryTimeRange) => void;
}

// Interpretation band colors (matching MarketHeatGauge)
const BAND_COLORS = {
  Cold: { bg: 'rgba(59, 130, 246, 0.15)', border: '#3b82f6' },         // Blue 0-20
  Cool: { bg: 'rgba(96, 165, 250, 0.15)', border: '#60a5fa' },         // Light Blue 20-40
  Neutral: { bg: 'rgba(252, 211, 77, 0.15)', border: '#fcd34d' },      // Yellow 40-60
  Warm: { bg: 'rgba(245, 158, 11, 0.15)', border: '#f59e0b' },         // Orange 60-75
  Hot: { bg: 'rgba(239, 68, 68, 0.15)', border: '#ef4444' },           // Red 75-90
  Overheated: { bg: 'rgba(185, 28, 28, 0.15)', border: '#b91c1c' },    // Dark Red 90-100
};

// Custom plugin to draw colored background bands
const backgroundBandsPlugin: Plugin<'line'> = {
  id: 'backgroundBands',
  beforeDraw: (chart) => {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales.y) return;

    const { left, right } = chartArea;
    const yScale = scales.y;

    // Band definitions: [min, max, color]
    const bands: [number, number, string][] = [
      [0, 20, BAND_COLORS.Cold.bg],
      [20, 40, BAND_COLORS.Cool.bg],
      [40, 60, BAND_COLORS.Neutral.bg],
      [60, 75, BAND_COLORS.Warm.bg],
      [75, 90, BAND_COLORS.Hot.bg],
      [90, 100, BAND_COLORS.Overheated.bg],
    ];

    bands.forEach(([min, max, color]) => {
      const yTop = yScale.getPixelForValue(max);
      const yBottom = yScale.getPixelForValue(min);

      ctx.fillStyle = color;
      ctx.fillRect(left, Math.min(yTop, yBottom), right - left, Math.abs(yBottom - yTop));
    });
  },
};

const MarketHeatHistoryChart: React.FC<MarketHeatHistoryChartProps> = ({
  data,
  ethPriceData,
  loading,
  error,
  timeRange,
  onTimeRangeChange,
}) => {
  // Format timestamp based on time range
  const formatLabel = useMemo(() => {
    return (timestamp: number): string => {
      switch (timeRange) {
        case '24h':
          return format(timestamp, 'HH:mm');
        case '7d':
          return format(timestamp, 'EEE HH:mm');
        case '30d':
          return format(timestamp, 'MMM d');
        case '90d':
          return format(timestamp, 'MMM d');
        case '1y':
          return format(timestamp, 'MMM d');
        case 'all':
          return format(timestamp, 'MMM yyyy');
        default:
          return format(timestamp, 'MMM d');
      }
    };
  }, [timeRange]);

  // Get line color based on current score
  const getLineColor = (score: number): string => {
    if (score <= 20) return BAND_COLORS.Cold.border;
    if (score <= 40) return BAND_COLORS.Cool.border;
    if (score <= 60) return BAND_COLORS.Neutral.border;
    if (score <= 75) return BAND_COLORS.Warm.border;
    if (score <= 90) return BAND_COLORS.Hot.border;
    return BAND_COLORS.Overheated.border;
  };

  // Sort data once and memoize
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => a.timestamp - b.timestamp);
  }, [data]);

  // Align ETH price data with heat score timestamps
  const alignedEthPrices = useMemo(() => {
    if (ethPriceData.length === 0 || sortedData.length === 0) return [];

    // For each heat score timestamp, find the closest ETH price
    return sortedData.map((heatEntry) => {
      const targetTime = heatEntry.timestamp;
      let closest = ethPriceData[0];
      let closestDiff = Math.abs(ethPriceData[0].timestamp - targetTime);

      for (const priceEntry of ethPriceData) {
        const diff = Math.abs(priceEntry.timestamp - targetTime);
        if (diff < closestDiff) {
          closest = priceEntry;
          closestDiff = diff;
        }
      }

      return closest.price;
    });
  }, [ethPriceData, sortedData]);

  // Prepare chart data
  const chartData = useMemo(() => {
    const latestScore = sortedData.length > 0 ? sortedData[sortedData.length - 1].overall : 50;

    const datasets: any[] = [
      {
        label: 'Market Heat Score',
        data: sortedData.map((entry) => entry.overall),
        borderColor: getLineColor(latestScore),
        backgroundColor: `${getLineColor(latestScore)}33`,
        borderWidth: 2,
        pointRadius: sortedData.length > 50 ? 0 : 3,
        pointHoverRadius: 5,
        tension: 0.3,
        fill: false,
        yAxisID: 'y',
      },
    ];

    // Add ETH price dataset if we have data
    if (alignedEthPrices.length > 0) {
      datasets.push({
        label: 'ETH Price',
        data: alignedEthPrices,
        borderColor: '#627EEA', // Ethereum blue
        backgroundColor: 'rgba(98, 126, 234, 0.1)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.3,
        fill: false,
        yAxisID: 'y1',
        borderDash: [5, 5], // Dashed line to differentiate
      });
    }

    return {
      labels: sortedData.map((entry) => formatLabel(entry.timestamp)),
      datasets,
    };
  }, [sortedData, formatLabel, alignedEthPrices]);

  // Chart options - memoized to update when dependencies change
  const options: ChartOptions<'line'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          color: '#8a92b2',
          usePointStyle: true,
          padding: 15,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        displayColors: true,
        callbacks: {
          title: (context) => {
            if (context[0] && sortedData[context[0].dataIndex]) {
              return format(sortedData[context[0].dataIndex].timestamp, 'PPpp');
            }
            return '';
          },
          label: (context) => {
            const idx = context.dataIndex;
            if (context.dataset.label === 'Market Heat Score') {
              const entry = sortedData[idx];
              return `Heat: ${context.raw}/100 (${entry?.interpretation || ''})`;
            } else if (context.dataset.label === 'ETH Price') {
              return `ETH: $${Number(context.raw).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            }
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: timeRange === '24h' ? 8 : timeRange === '7d' ? 7 : timeRange === '1y' || timeRange === 'all' ? 12 : 10,
          color: '#8a92b2',
          autoSkip: true,
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        min: 0,
        max: 100,
        grid: {
          color: 'rgba(138, 146, 178, 0.1)',
        },
        ticks: {
          stepSize: 20,
          color: '#8a92b2',
          callback: (value) => `${value}`,
        },
        title: {
          display: true,
          text: 'Heat Score',
          color: '#8a92b2',
        },
      },
      y1: {
        type: 'linear' as const,
        display: alignedEthPrices.length > 0,
        position: 'right' as const,
        grid: {
          drawOnChartArea: false, // Don't draw grid lines for secondary axis
        },
        ticks: {
          color: '#627EEA',
          callback: (value) => `$${Number(value).toLocaleString()}`,
        },
        title: {
          display: true,
          text: 'ETH Price',
          color: '#627EEA',
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  }), [sortedData, timeRange, alignedEthPrices.length]);

  const timeRanges: HistoryTimeRange[] = ['24h', '7d', '30d', '90d', '1y', 'all'];

  return (
    <div className="bg-dashboard-card-light dark:bg-dashboard-card-dark rounded-card shadow-card p-6">
      {/* Header with time range selector */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <h3 className="text-lg font-semibold">Score History</h3>
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          {timeRanges.map((range) => (
            <button
              key={range}
              onClick={() => onTimeRangeChange(range)}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                timeRange === range
                  ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Chart container */}
      <div className="relative h-64 sm:h-72">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-pulse flex flex-col items-center">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading history...</span>
            </div>
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-red-500 text-lg">Error loading history</span>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{error}</p>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-gray-500 dark:text-gray-400 text-lg">No history data yet</span>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                History will appear as scores are recorded
              </p>
            </div>
          </div>
        ) : (
          <Line
            key={`chart-${timeRange}-${sortedData.length}`}
            data={chartData}
            options={options}
            plugins={[backgroundBandsPlugin]}
          />
        )}
      </div>

      {/* Legend for interpretation bands */}
      {!loading && !error && data.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-3 justify-center text-xs">
          {Object.entries(BAND_COLORS).map(([name, colors]) => (
            <div key={name} className="flex items-center gap-1">
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: colors.border }}
              ></div>
              <span className="text-gray-600 dark:text-gray-400">{name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketHeatHistoryChart;
